/**
 * Owner: Masa Bayu
 * File: public-author-data.ts
 * Date:Time: 2026-10-23 01:20
 * Description: Projek author awam untuk /author/:id — HANYA apa yang manifest hub
 *   benar-benar hantar: nama, buku, genre (§405) dan kiraan yang boleh deriva
 *   daripada senarai buku itu. §466 S1: bio rekaan, emel `.@qxread.com` yang
 *   dijana daripada nama, potret ilustrasi dan kiraan pengikut hash dibuang —
 *   paparan Follow yang wujud ialah simpanan setempat per-peranti, bukan angka
 *   global.
 */

import {
  type MarketplaceBook,
  type MarketplaceCategoryId,
} from '../../home/marketplace/marketplace-projection';

export interface PublicAuthor {
  readonly id: string;
  readonly name: string;
  readonly initials: string;
  /** null = hub belum menghantar bio; UI memaparkan mesej kosong yang jujur. */
  readonly bio: string | null;
  /** Pautan profil dalaman QXRead sendiri — benar, bukan web luar rekaan. */
  readonly website: string;
  readonly books: readonly MarketplaceBook[];
  readonly genres: readonly MarketplaceCategoryId[];
  readonly topBook: MarketplaceBook | null;
}

function stripHonorific(name: string): string {
  return name.replace(/^(dr\.?|prof\.?madya|prof\.?|cikgu|ustazah|ustaz)\s+/i, '');
}

export function slugifyAuthorName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function websiteOf(id: string): string {
  return `https://qxread.com/author/${id}`;
}

function initialsOf(name: string): string {
  const words = stripHonorific(name)
    .split(/\s+/)
    .filter((word) => word.length > 0);
  const first = words[0] ?? name;
  const last = words.length > 1 ? (words[words.length - 1] ?? '') : '';
  const letters = `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  return letters.length > 0 ? letters : 'QX';
}

/** Terbitan terkini; buku tanpa tarikh hub kalah dan ikut tajuk (§466 S1). */
function isNewer(a: MarketplaceBook, b: MarketplaceBook): boolean {
  if (a.publishedAt !== null && b.publishedAt !== null) return a.publishedAt > b.publishedAt;
  if (a.publishedAt !== null && b.publishedAt === null) return true;
  if (b.publishedAt !== null && a.publishedAt === null) return false;
  return a.title.localeCompare(b.title) < 0;
}

/** Susunan tetap profil: terbitan terkini dahulu (menggantikan kedudukan rating). */
export function byRecencyDesc(a: MarketplaceBook, b: MarketplaceBook): number {
  if (isNewer(a, b)) return -1;
  if (isNewer(b, a)) return 1;
  return 0;
}

export function buildPublicAuthor(name: string, books: readonly MarketplaceBook[]): PublicAuthor {
  const genres: MarketplaceCategoryId[] = [];
  let topBook: MarketplaceBook | null = null;
  for (const book of books) {
    if (book.categoryId !== null && !genres.includes(book.categoryId)) {
      genres.push(book.categoryId);
    }
    if (topBook === null || isNewer(book, topBook)) {
      topBook = book;
    }
  }
  const id = slugifyAuthorName(name);
  return {
    id,
    name,
    initials: initialsOf(name),
    bio: null,
    website: websiteOf(id),
    books,
    genres,
    topBook,
  };
}

export function listPublicAuthors(books: readonly MarketplaceBook[]): readonly PublicAuthor[] {
  const byName = new Map<string, MarketplaceBook[]>();
  for (const book of books) {
    // §466 S1 — manifest tanpa penulis tidak membentuk profil author palsu.
    if (book.author === null) continue;
    const group = byName.get(book.author);
    if (group !== undefined) {
      group.push(book);
    } else {
      byName.set(book.author, [book]);
    }
  }
  return [...byName.entries()]
    .map(([name, group]) => buildPublicAuthor(name, group))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function resolvePublicAuthor(
  authorId: string,
  books: readonly MarketplaceBook[],
): PublicAuthor | null {
  const decoded = safeDecode(authorId);
  if (decoded === null) {
    return null;
  }
  const match = listPublicAuthors(books).find((author) => author.id === slugifyAuthorName(decoded));
  return match ?? null;
}

function safeDecode(value: string): string | null {
  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
}
